/**
 * useTranscriptReflowAnchor — keep the reader on the same MESSAGE when the
 * pane changes width (2026-09-14).
 *
 * THE GAP. `matchMedia('(max-width: 820px)')` was the chat's only viewport
 * signal, and all it does is flip the layout mode. There is no `resize` and
 * no `orientationchange` listener anywhere in the transcript's scroll path,
 * so:
 *
 *   - dragging a desktop window 1200 → 900px repositions nothing at all;
 *   - crossing 820px moves the pane between docked and fullscreen, every
 *     bubble reflows to a new height, and the reader's pixel `scrollTop` now
 *     points at a different message;
 *   - rotating a phone, and toggling docked / floating / fullscreen, are the
 *     same event with the same outcome.
 *
 * All four are one thing: THE SCROLLER'S WIDTH CHANGED. So this observes that
 * one thing rather than instrumenting four triggers, and it restores by
 * MESSAGE rather than by pixel, because the pixel is exactly what stopped
 * being meaningful.
 *
 * WHY IT DOES NOT CAPTURE ITS OWN ANCHOR. A ResizeObserver fires after
 * layout, so by the time this knows the width changed, the pre-reflow
 * position is gone. The anchor therefore comes from the persisted-view
 * writer, which already walks the visible rows on a debounce and already
 * computes exactly this (the topmost visible message and its offset) — it
 * just also writes it to storage. Reusing its output costs nothing, and at
 * most 500ms of staleness, which is no staleness at all for a reader who has
 * not scrolled.
 *
 * PINNED READERS ARE NOT THIS HOOK'S BUSINESS. The scroll controller's own
 * ResizeObserver already re-sticks them, and two correctors writing the same
 * scrollTop is the classic jump-back bug. Not double-correcting is the
 * contract, not an oversight.
 *
 * Height-only changes are ignored for the same reason — that is the keyboard,
 * and it is handled by shrinking the pane (see keyboardInset.ts).
 */
import { useEffect, useRef, type RefObject } from 'react';

/**
 * How long to keep re-applying after a width change.
 *
 * NOT a guess: the pane's own transform / max-height transitions run 0.4s and
 * its mode-settle keyframe 0.35s, so a single apply at the first observer tick
 * lands on geometry that is still moving. Re-applying until the animation
 * settles is the honest fix, and it is the same reasoning the Chromium layout
 * gate uses when it waits for `transform === 'none'` instead of sleeping.
 */
export const REFLOW_SETTLE_MS = 600;

/** Below this, a width change is sub-pixel noise, not a reflow. */
export const REFLOW_WIDTH_EPSILON_PX = 1;

export interface ReflowAnchor {
  /** `perDocMessageId` of the topmost visible message. */
  anchorMessageId: string;
  /** Its top, relative to the scroller's top, when it was sampled. */
  offsetPx: number;
}

export interface UseTranscriptReflowAnchorArgs {
  enabled: boolean;
  scrollerRef: RefObject<HTMLElement | null>;
  /** The most recent sample from the view-marker writer, or null. */
  anchorRef: RefObject<ReflowAnchor | null>;
  /** Live pinned state. A pinned reader is the controller's to re-stick. */
  isPinnedNow: () => boolean;
  /** Resolve a message id to its rendered row, or null. */
  findRow: (anchorMessageId: string) => HTMLElement | null;
}

export function useTranscriptReflowAnchor({
  enabled,
  scrollerRef,
  anchorRef,
  isPinnedNow,
  findRow,
}: UseTranscriptReflowAnchorArgs): void {
  const lastWidthRef = useRef<number | null>(null);
  const settleUntilRef = useRef(0);
  // Live handles, so the observer is built once and never captures a stale
  // closure — the same ref idiom the scroll controller uses for its
  // classifier.
  const isPinnedRef = useRef(isPinnedNow);
  isPinnedRef.current = isPinnedNow;
  const findRowRef = useRef(findRow);
  findRowRef.current = findRow;

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof ResizeObserver === 'undefined') return undefined;
    const el = scrollerRef.current;
    if (!el) return undefined;

    const apply = (): void => {
      const anchor = anchorRef.current;
      if (!anchor) return;
      const row = findRowRef.current(anchor.anchorMessageId);
      // A miss leaves the viewport alone rather than falling back to the
      // bottom: yanking a reader to the tail because a row could not be found
      // is worse than the drift the miss causes.
      if (!row) return;
      const rowRect = row.getBoundingClientRect();
      if (rowRect.height === 0) return;
      const delta = (rowRect.top - el.getBoundingClientRect().top) - anchor.offsetPx;
      if (Math.abs(delta) < 1) return;
      el.scrollTop += delta;
    };

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width ?? el.clientWidth;
      const previous = lastWidthRef.current;
      lastWidthRef.current = width;
      const now = Date.now();

      if (previous !== null && Math.abs(width - previous) >= REFLOW_WIDTH_EPSILON_PX) {
        // Arm only for an unpinned reader — see the docblock.
        if (!isPinnedRef.current()) settleUntilRef.current = now + REFLOW_SETTLE_MS;
      }
      // Re-apply on every tick until the pane's transitions settle, including
      // ticks with no width delta: those are the animation's own frames, and
      // they are the ones carrying the geometry toward its final value.
      if (now <= settleUntilRef.current && !isPinnedRef.current()) apply();
    });

    observer.observe(el);
    return () => {
      observer.disconnect();
      lastWidthRef.current = null;
      settleUntilRef.current = 0;
    };
  }, [enabled, scrollerRef, anchorRef]);
}
